import React from 'react';
import { Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TabbedOverviewPage, SectionHeader } from '../components/OverviewSections.comp';
import { NAVY, headCell, bodyCell } from '../components/widgets.comp';

/* Slide 34 — How Priority Actions Are Triggered — plus its linked doc
   (https://docs.google.com/document/d/184t8WL-PUC12cK8cpXn4PdvR6Pk6-e8YnS0Dyvihe_Q),
   which spells out the Action Plan Families and each family's Full Action Plan. */
const DECISION_LOGIC = [
    'Competencies gap → PbDQ Learning Program.',
    'Parent context gap → Parental Engagement.',
    'Teacher or school education gap → Teacher Empowerment.',
    'Serious risk or weak help-seeking → School Safeguarding & Support.',
    'Missing stakeholder evidence → Assessment Package.',
];

const ACTION_SCALE_LOGIC = [
    'One class affected → class action.',
    'Several classes in one grade → grade action.',
    'Pattern across school → whole-school action.',
    'Sensitive or serious risk → safeguarding-first action.',
];

const ACTION_PLAN_FAMILIES = [
    {code: 'PL', family: 'PbDQ Learning Program',          purpose: 'Improve child online safety readiness through targeted learning'},
    {code: 'PE', family: 'Parental Engagement',             purpose: 'Strengthen parent mediation, safety conversations, and home support'},
    {code: 'TE', family: 'Teacher Empowerment',             purpose: 'Improve teacher ability to teach, identify, respond, and refer'},
    {code: 'SS', family: 'School Safeguarding & Support',   purpose: 'Protect and support students where risk, distress, victimization, or help-seeking gaps appear'},
];

const FULL_ACTION_PLANS = [
    {
        heading: 'A. PbDQ Learning Program — PL',
        rows: [
            {code: 'PL', type: 'Create targeted learning program', trigger: 'Readiness areas are low and related risks are high', action: 'Create a learning program mapped to the weak readiness areas, items, and the corresponding learning outcomes'},
        ],
    },
    {
        heading: 'B. Parental Engagement — PE',
        rows: [
            {code: 'PE1', type: 'Run parent assessment', trigger: 'Parent data is missing or the home context is unclear', action: 'Deploy the COS Parent Pack to assess parent mediation, home safety practice, and digital parenting readiness'},
            {code: 'PE2', type: 'Host parent workshop', trigger: 'Parental mediation is low, safety conversations at home are infrequent, or related risk is high', action: 'Run a parent workshop on safety conversations, screen-use boundaries, AI risks, privacy, cyberbullying, and reporting'},
            {code: 'PE3', type: 'Send home safety checklist', trigger: 'Home rules or safety practices are inconsistent', action: 'Provide a checklist covering devices, privacy settings, AI use, stranger contact, reporting, and screen routines'},
            {code: 'PE4', type: 'Targeted parent briefing', trigger: 'A sensitive or serious risk appears', action: 'Brief the affected parent group on contact risk, image misuse, reporting routes, and support options'},
        ],
    },
    {
        heading: 'C. Teacher Empowerment — TE',
        rows: [
            {code: 'TE1', type: 'Run teacher assessment', trigger: 'Teacher readiness is unknown, or students report weak school-based child online safety education', action: 'Deploy the COS Teacher Pack to assess risk identification, prevention, response, and classroom child online safety education'},
            {code: 'TE2', type: 'Teacher capability training and resource', trigger: 'Classroom teaching needs strengthening, or identified risks require a curriculum response', action: 'Train teachers on child online safety, cyber risk recognition, privacy, misinformation, cyberbullying, and AI image misuse, and provide the matching classroom resources'},
            {code: 'TE3', type: 'Response protocol training and referral pathway guide', trigger: 'Cybervictimization, unwanted contact, AI image misuse, or help-seeking gaps appear, and staff lack clarity on escalation', action: 'Train staff to listen, document, support, and refer when a student reports harm, following a defined pathway through pastoral lead, safeguarding lead, counselor, parent contact, and external agency'},
        ],
    },
    {
        heading: 'D. School Safeguarding & Support — SS',
        rows: [
            {code: 'SS1', type: 'Run school leader assessment with safety review', trigger: 'Curriculum, policy, governance, reporting, or safeguarding readiness is unknown, or a serious or sensitive risk appears', action: 'Deploy the COS School Leader Pack to assess curriculum, policies, governance, reporting pathways, safeguarding practice, and review the aggregate pattern to determine support or escalation'},
            {code: 'SS2', type: 'Counselling / pastoral support group', trigger: 'AI-digital distress, emotional reliance, cybervictimization, or addiction risk is elevated', action: 'Set up a wellbeing group, trusted-adult check-ins, pastoral support, and an onward referral route'},
            {code: 'SS3', type: 'Reporting pathway campaign', trigger: 'Student trust in getting help is low, or victimization is high', action: 'Publish the reporting pathway on how to report, what happens after a report, who students can speak to, and the assurance that reporting carries no blame'},
            {code: 'SS4', type: 'Prevention campaign', trigger: 'Risk is widespread across grades or driven by peer norms', action: 'Run a school-wide campaign on screen time, cyberbullying, AI image misuse, misinformation, privacy, or contact risk'},
            {code: 'SS5', type: 'External referral protocol', trigger: 'Risk may involve illegality, coercion, sexual exploitation, extortion, threats, stalking, or grooming', action: 'Activate the referral pathway to child protection, police, or a specialist agency once the threshold is met'},
            {code: 'SS6', type: 'Policy / governance update', trigger: 'Repeated risk patterns indicate a policy gap', action: 'Update the acceptable use policy, AI use rules, image-sharing rules, reporting protocol, or parent communication protocol'},
        ],
    },
];

const LogicCard = ({title, items}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 2}}>{title}</Typography>
        <Box component="ul" sx={{m: 0, pl: 2.2}}>
            {items.map((item) => (
                <Box component="li" key={item} sx={{mb: 1}}>
                    <Typography sx={{fontSize: '13px', color: '#4B5563', lineHeight: 1.6}}>{item}</Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

const FamiliesTable = () => (
    <TableContainer sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{...headCell, textAlign: 'left', pl: 2.5, width: '8%'}}>Code</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left', width: '25%'}}>Action Family</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left'}}>Main Purpose</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {ACTION_PLAN_FAMILIES.map((row, i) => (
                    <TableRow key={row.code} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                        <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.code}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.family}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.purpose}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const ActionPlanTable = ({rows}) => (
    <TableContainer sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 3,
    }}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{...headCell, textAlign: 'left', pl: 2.5, width: '8%'}}>Code</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left', width: '18%'}}>Action Type</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left', width: '32%'}}>Trigger</TableCell>
                    <TableCell sx={{...headCell, textAlign: 'left'}}>Concrete Action</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, i) => (
                    <TableRow key={row.code} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                        <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5, verticalAlign: 'top'}}>{row.code}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left', verticalAlign: 'top'}}>{row.type}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left', verticalAlign: 'top'}}>{row.trigger}</TableCell>
                        <TableCell sx={{...bodyCell, textAlign: 'left', verticalAlign: 'top'}}>{row.action}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const PriorityActionsSection = () => (
    <Box>
        <SectionHeader
            title="How Priority Actions Are Triggered"
            subtitle="How is the baseline evidence translated into action families, delivery scale, and concrete interventions?"
        />

        {/*<Grid container spacing={3} alignItems="stretch" sx={{mb: 4}}>*/}
        {/*    <Grid item xs={12} md={6}>*/}
        {/*        <LogicCard title="Decision logic" items={DECISION_LOGIC}/>*/}
        {/*    </Grid>*/}
        {/*    <Grid item xs={12} md={6}>*/}
        {/*        <LogicCard title="Action scale logic" items={ACTION_SCALE_LOGIC}/>*/}
        {/*    </Grid>*/}
        {/*</Grid>*/}

        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
            Action Plan Families
        </Typography>
        <Box sx={{mb: 4}}>
            <FamiliesTable/>
        </Box>

        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 2}}>
            Full Action Plans
        </Typography>
        {FULL_ACTION_PLANS.map((plan) => (
            <Box key={plan.heading} sx={{mb: 3}}>
                <Typography sx={{fontSize: '15px', fontWeight: 700, color: '#4B5563', mb: 1.5}}>
                    {plan.heading}
                </Typography>
                <ActionPlanTable rows={plan.rows}/>
            </Box>
        ))}
    </Box>
);

const MethodologyPriorityActionsPage = () => (
    <TabbedOverviewPage
        skipNoDataGate
        tabs={[
            {
                label: 'How Priority Actions Are Triggered',
                content: <PriorityActionsSection/>,
            },
        ]}
    />
);

export default MethodologyPriorityActionsPage;
